import { NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import {
  LayoutDashboard,
  FolderInput,
  Library as LibIcon,
  Users,
  Settings,
  Newspaper,
  LogOut,
  Home,
  Calendar,
  FileEdit,
  BookText,
  Landmark,
  KeyRound,
} from "lucide-react";

const links = [
  { to: "/dashboard", label: "Vue d'ensemble", Icon: LayoutDashboard, roles: ["super_admin", "deposits_service", "library", "archives", "doctoral_school", "cecom"], end: true },
  { to: "/dashboard/depots", label: "Dépôts académiques", Icon: FolderInput, roles: ["super_admin", "deposits_service"] },
  { to: "/dashboard/codes-etudiants", label: "Codes d'accès étudiants", Icon: KeyRound, roles: ["super_admin", "deposits_service"] },
  { to: "/dashboard/bibliotheque", label: "Bibliothèque & Archives", Icon: LibIcon, roles: ["super_admin", "library"] },
  { to: "/dashboard/publications", label: "Publications numériques", Icon: BookText, roles: ["super_admin", "library"] },
  { to: "/dashboard/musees", label: "Musées (galeries)", Icon: Landmark, roles: ["super_admin", "library"] },
  { to: "/dashboard/abonnements", label: "Demandes d'abonnement", Icon: KeyRound, roles: ["super_admin", "library"] },
  { to: "/dashboard/contenu", label: "Contenu des pages", Icon: FileEdit, roles: ["super_admin"] },
  { to: "/dashboard/reglages", label: "Réglages du portail", Icon: Settings, roles: ["super_admin"] },
  { to: "/dashboard/actualites", label: "Actualités", Icon: Newspaper, roles: ["super_admin"] },
  { to: "/dashboard/annees", label: "Années académiques", Icon: Calendar, roles: ["super_admin"] },
  { to: "/dashboard/listes", label: "Listes de référence", Icon: Settings, roles: ["super_admin"] },
  { to: "/dashboard/utilisateurs", label: "Utilisateurs", Icon: Users, roles: ["super_admin"] },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();

  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Chargement...</div>;
  }
  if (!user) {
    return <Navigate to="/connexion" state={{ from: location.pathname }} replace />;
  }
  const role = user.role;
  const visible = links.filter((l) => l.roles.includes(role));

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="dashboard-layout">
      <aside className="w-64 bg-unisic-blue-800 text-slate-100 min-h-screen sticky top-0 flex flex-col" style={{ background: "#082646" }}>
        <div className="px-5 py-5 border-b border-white/10">
          <Logo variant="light" />
        </div>
        <div className="px-3 py-4 flex-1 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-widest text-white/40 px-3 mb-2">Navigation</div>
          <nav className="space-y-1">
            {visible.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) => `dash-sidebar-link ${isActive ? "active" : ""}`}
                data-testid={`sidebar-${l.to.split("/").pop() || "root"}`}
              >
                <l.Icon className="w-4 h-4" />
                {l.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="border-t border-white/10 p-3 text-xs">
          <div className="px-3 py-2 text-slate-300">
            <div className="font-semibold text-white">{user.name || user.email}</div>
            <div className="text-slate-400 truncate">{user.email}</div>
            <div className="mt-1 inline-block text-[10px] px-2 py-0.5 rounded-full bg-unisic-gold/20 text-unisic-gold uppercase tracking-widest">
              {role}
            </div>
          </div>
          <div className="mt-2 space-y-1">
            <NavLink to="/" className="dash-sidebar-link" data-testid="sidebar-home">
              <Home className="w-4 h-4" /> Portail public
            </NavLink>
            <button className="dash-sidebar-link w-full" onClick={logout} data-testid="sidebar-logout">
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="p-8 lg:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
